import React, { useState } from 'react';
import { ArrowRight, Flame, Footprints, Droplets } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, deserveWellData, releasePainData, servicesData } from '../data/mockData';

const iconMap = {
  flame: Flame,
  footprints: Footprints,
  droplets: Droplets,
};

const ServiceIcon = ({ src, iconType, title }) => {
  const [imgLoaded, setImgLoaded] = useState(true);
  const FallbackIcon = iconMap[iconType] || Flame;
  
  if (imgLoaded) {
    return (
      <img
        src={src}
        alt={title}
        className="w-16 h-16 object-contain"
        onError={() => setImgLoaded(false)}
      />
    );
  }
  
  return <FallbackIcon size={48} className="text-[#C9A84C]" strokeWidth={1.5} />;
};

const FeaturesSections = () => {
  return (
    <>
      {/* You Deserve to Feel Well */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="w-24 h-[3px] bg-[#C9A84C] mb-8" />
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-seaweed text-[#C9A84C] text-3xl mb-3">
                {deserveWellData.label}
              </p>
              <h2 className="font-playfair text-[#1a1a1a] text-3xl md:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
                {deserveWellData.heading}
              </h2>
              {deserveWellData.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="font-open-sans text-[#666] text-[15px] leading-relaxed mb-4"
                >
                  {p}
                </p>
              ))}
              <Link
                to="/services"
                className="inline-block mt-6 px-8 py-3 bg-[#C9A84C] text-white font-open-sans text-xs tracking-[0.2em] uppercase hover:bg-[#B89740] transition-colors duration-300 hover:shadow-lg"
              >
                {deserveWellData.buttonText}
              </Link>
            </div>
            <div className="relative">
              <img
                src={images.feelWell}
                alt="Your Colorado Springs non-invasive pain relief experts"
                className="relative z-10 w-full h-auto shadow-xl rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Release the Pain */}
      <section id="techniques" className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Image with built-in decorative frame */}
            <div className="relative">
              <img
                src={images.servicesImg}
                alt="Healing touch therapy"
                className="w-full h-auto"
              />
            </div>

            {/* Text content */}
            <div>
              <p className="font-seaweed text-[#C9A84C] text-3xl md:text-4xl mb-1">
                {releasePainData.scriptLabel}
              </p>
              <h2 className="font-playfair text-[#1a1a1a] text-3xl md:text-4xl lg:text-5xl font-bold mb-8 leading-tight">
                {releasePainData.heading}
              </h2>
              <p className="font-open-sans text-[#999] text-lg leading-relaxed mb-8">
                {releasePainData.intro}
              </p>
              {releasePainData.sections.map((section, i) => (
                <div key={i} className="mb-8">
                  <h3 className="font-open-sans text-[#1a1a1a] text-sm tracking-[0.2em] font-bold mb-3">
                    {section.title}
                  </h3>
                  <p className="font-open-sans text-[#888] text-sm leading-relaxed">
                    {section.text}
                  </p>
                </div>
              ))}
              <a
                href="#"
                className="inline-flex items-center gap-3 text-[#C9A84C] font-open-sans text-xs tracking-[0.2em] uppercase hover:text-[#B89740] transition-colors duration-300"
              >
                {releasePainData.buttonText}
                <span className="text-lg">—</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Our Amazing Services */}
      <section id="services" className="py-16 md:py-24 px-4 bg-[#F3ECF8]">
        <div className="max-w-6xl mx-auto text-center">
          <p className="font-seaweed text-[#C9A84C] text-3xl md:text-4xl mb-1">
            {servicesData.scriptLabel}
          </p>
          <h2 className="font-playfair text-[#1a1a1a] text-3xl md:text-4xl lg:text-5xl font-bold mb-16 leading-tight">
            {servicesData.heading}
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {servicesData.services.map((service, i) => (
              <div
                key={i}
                className="bg-white rounded-sm p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 group flex flex-col items-center text-center"
              >
                <div className="w-20 h-20 mb-6 flex items-center justify-center">
                  <ServiceIcon src={service.icon} iconType={service.iconType} title={service.title} />
                </div>
                <h3 className="font-open-sans text-[#C9A84C] text-sm tracking-[0.15em] font-bold mb-4">
                  {service.title}
                </h3>
                <p className="font-open-sans text-[#888] text-sm leading-relaxed mb-6 flex-1">
                  {service.description}
                </p>
                <button className="text-[#C9A84C] hover:text-[#B89740] transition-colors duration-300">
                  <ArrowRight size={24} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default FeaturesSections;
